import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ArrowRight, Trash2 } from "lucide-react";

export default function Courses() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", level: "Beginner", category: "Marketing", status: "DRAFT", thumbnail_url: "" });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const load = async () => { const { data } = await api.get("/courses"); setItems(data.items); };
    useEffect(() => { load(); }, []);

    const confirmDelete = async () => {
        await api.delete(`/courses/${deleteTarget.id}`);
        toast.success("Course deleted");
        setDeleteTarget(null);
        setDeleteConfirm("");
        load();
    };

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/courses", form);
            toast.success("Course created");
            setOpen(false);
            setForm({ title: "", description: "", level: "Beginner", category: "Marketing", status: "DRAFT", thumbnail_url: "" });
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="space-y-6" data-testid="courses-admin-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Catalog</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Courses</h1>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="mx-btn-primary" data-testid="add-course-btn"><Plus className="h-4 w-4 mr-2" /> New course</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Create course</DialogTitle></DialogHeader>
                        <form onSubmit={create} className="space-y-3">
                            <div><Label>Title</Label><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="course-title-input" /></div>
                            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="course-desc-input" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Level</Label><Input value={form.level} onChange={e => setForm({ ...form, level: e.target.value })} /></div>
                                <div><Label>Category</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
                            </div>
                            <div><Label>Thumbnail URL</Label><Input value={form.thumbnail_url} onChange={e => setForm({ ...form, thumbnail_url: e.target.value })} /></div>
                            <DialogFooter><Button type="submit" className="mx-btn-primary" data-testid="course-create-submit">Create</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {items.map(c => (
                    <Card key={c.id} className="mx-card overflow-hidden" data-testid={`admin-course-card-${c.id}`}>
                        <div className="aspect-video bg-muted overflow-hidden">
                            {c.thumbnail_url && <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" />}
                        </div>
                        <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-[10px]">{c.level}</Badge>
                                <Badge className={c.status === "PUBLISHED" ? "bg-[#16A34A] hover:bg-[#16A34A] text-[10px]" : "text-[10px]"}>{c.status}</Badge>
                            </div>
                            <h3 className="font-bold text-base">{c.title}</h3>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                            <div className="flex gap-2 mt-4">
                                <Link to={`/admin/courses/${c.id}`} className="flex-1">
                                    <Button variant="outline" className="w-full rounded-xl" data-testid={`edit-course-${c.id}`}>Edit curriculum <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
                                </Link>
                                <Button variant="ghost" size="icon" className="rounded-xl border border-border" onClick={() => { setDeleteTarget(c); setDeleteConfirm(""); }} data-testid={`delete-course-${c.id}`}>
                                    <Trash2 className="h-4 w-4 text-[#E11D48]" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteConfirm(""); } }}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Delete course</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            This will permanently delete <span className="font-semibold text-foreground">{deleteTarget?.title}</span> along with all its modules and lessons. This cannot be undone.
                        </p>
                        <div>
                            <Label>Type the course name to confirm</Label>
                            <Input
                                className="mt-1"
                                value={deleteConfirm}
                                onChange={e => setDeleteConfirm(e.target.value)}
                                placeholder={deleteTarget?.title}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirm(""); }}>Cancel</Button>
                        <Button
                            disabled={deleteConfirm !== deleteTarget?.title}
                            onClick={confirmDelete}
                            className="bg-[#E11D48] hover:bg-[#BE123C] text-white"
                        >
                            Delete permanently
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
